import { useState } from 'react';

const institutions = [
  {
    name: 'Harvard Business School AI Institute',
    path: 'harvard-business-school',
    role: 'Predoctoral Research Associate',
    period: '2025 – Present',
    projects: [
      {
        title: 'Beyond a Technology Lens: Characterizing the Problems Entrepreneurs Solve',
        filename: 'beyond_tech_lens.py',
        date: 'Ongoing',
        tags: ['Python', 'LLMs', 'NLP'],
        description: 'Used LLMs to extract structured, solution-agnostic problem statements from descriptions of ~89,000 U.S. startups. Applied a top-K sparse autoencoder to identify recurring themes, creating the first large-scale taxonomy of U.S. startups based on customer-problem similarity.',
        link: 'https://papers.ssrn.com/sol3/papers.cfm?abstract_id=7339099',
        linkLabel: '↗ HBS Working Paper No 27-016',
      },
      {
        title: 'GRADE: A Human-Guided Research Loop for Validating LLM-generated Constructs',
        filename: 'grade_pipeline.py',
        date: 'Ongoing',
        tags: ['Python', 'LLMs', 'Human Evaluation'],
        description: 'Developing a human-AI pipeline to validate LLM-generated representations when ground truth is difficult to establish, pairing LLM retrieval-generation with structured expert interviews. Led a five-person team in 330 structured founder interviews to produce a founder-reviewed reference dataset for Beyond a Technology Lens.',
        link: 'https://github.com/mimixiaoyichen/grade-interface',
        linkLabel: '↗ GitHub',
      },
    ],
  },
  {
    name: 'Oxford Internet Institute',
    path: 'oxford-internet-institute',
    role: 'MSc Social Data Science · Distinction',
    period: '2024 – 2025',
    projects: [
      {
        title: 'Echoes of Division: Quantifying Structural and Narrative Polarisation in US Political Podcasts',
        filename: 'echoes_of_division.py',
        date: 'Spring 2025',
        distinction: true,
        tags: ['Python', 'Network Analysis', 'LLMs'],
        description: 'Built an end-to-end pipeline analyzing 2,900+ hours of political podcast audio combining ASR (Whisper) and LLMs (GPT-4o). Constructed bipartite host–guest relationship graphs and weighted unipartite host-host graphs to quantify affective polarisation, revealing asymmetric network cohesion and dominant intra-group narrative sharing in the 2020 and 2024 U.S. Presidential Elections.',
        link: '/docs/MIT_Poster_Final.pdf',
        linkLabel: '↗ MIT Polarization Workshop, 2025',
      },
      {
        title: 'Sexism Detection in the 2024 U.S. Presidential Election with DeBERTa',
        filename: 'sexism_classifier.py',
        date: 'Spring 2025',
        distinction: true,
        tags: ['Python', 'LLMs', 'NLP'],
        description: 'Fine-tuned DeBERTa-XL on comments from Reddit and Gab to detect binary and granular sexism. Pseudo-labeled 1.9 million tweets using an entropy-aware MC Dropout strategy to enable large-scale hierarchical classification of sexist content during the 2024 U.S. Presidential Election.',
        link: 'https://github.com/mimixiaoyichen/oii-nlp-summative',
        linkLabel: '↗ GitHub',
      },
      {
        title: 'Heterogeneity in Drivers of Higher Education Attainment: Evidence from 21st-century China',
        filename: 'china_education.R',
        date: 'Fall 2024',
        tags: ['R', 'Panel Data', 'Statistics'],
        description: 'Fixed-effects panel regression on provincial enrollment data (2005–2019) evaluating regional and temporal heterogeneity in higher education attainment across China. Identified urbanisation and regional GDP as the dominant drivers, with significant heterogeneous effects across economic regions and temporal stages.',
        link: '/docs/analyticalstats_summative.pdf',
        linkLabel: '↗ view paper',
      },
    ],
  },
  {
    name: 'New York University',
    path: 'new-york-university',
    role: 'B.A. Data Science · cum laude',
    period: '2020 – 2024',
    projects: [
      {
        title: 'Anime Genre Classification with Convolutional Neural Networks',
        filename: 'anime_classification.py',
        date: 'Winter 2023',
        tags: ['Python', 'CNN', 'Classification'],
        description: 'Multi-label genre classification of anime poster images comparing a custom CNN, transfer learning (ResNet50), semi-supervised learning, and AutoML. Achieved Hamming Loss of 0.087 with ResNet50 and evaluated 7 model architectures via AutoKeras hyperparameter tuning.',
        link: 'https://github.com/mimichengolf/anime-classification',
        linkLabel: '↗ GitHub',
      },
      {
        title: 'The Food Delivery Dilemma: Classification & Evaluation of Real vs. Ghost Kitchens',
        filename: 'ghost_kitchen_nlp.py',
        date: 'Spring 2023',
        tags: ['Python', 'NLP', 'Classification'],
        description: "Classification tool to identify ghost kitchens by analyzing linguistic patterns in menu item descriptions scraped from food delivery platforms using OpenAI's text-embedding-ada-002 and cosine similarity.",
        link: 'https://github.com/mimichengolf/nlp-research/blob/main/README.md',
        linkLabel: '↗ GitHub',
      },
      {
        title: 'Spotify Music Genre Classification',
        filename: 'spotify_classifier.py',
        date: 'Spring 2023',
        tags: ['Python', 'Machine Learning', 'Classification'],
        description: 'Multi-class genre classification pipeline for Spotify tracks using XGBoost on engineered audio features. Applied K-means clustering to explore genre overlaps and latent groupings, achieving ROC-AUC of 0.902.',
        link: 'https://github.com/mimichengolf/classification-capstone-project-fall-2023',
        linkLabel: '↗ GitHub',
      },
    ],
  },
];

function InstSection({ inst, defaultOpen, delay }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div
      className={`inst-section${open ? ' open' : ''}`}
      style={{ animationDelay: delay }}
    >
      <div
        className="inst-header"
        onClick={() => setOpen(!open)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setOpen(!open)}
      >
        <div className="inst-header-left">
          <span className="inst-name">{inst.name}</span>
          <span className="inst-path-cmd">
            <span className="inst-dollar">$</span> cd ~/{inst.path}/
          </span>
        </div>
        <div className="inst-header-right">
          <div className="inst-meta-right">
            <span className="inst-role">{inst.role}</span>
            <span className="inst-period">{inst.period}</span>
          </div>
          <span className="inst-count">{inst.projects.length} project{inst.projects.length !== 1 ? 's' : ''}</span>
          <span className="inst-toggle">{open ? '▾' : '▸'}</span>
        </div>
      </div>

      <div className="inst-body">
        <div className="inst-body-inner">
          {inst.projects.map((proj, i) => (
            <div className="proj-card" key={proj.filename}>
              <div className="proj-card-top">
                <span className="proj-index">[{String(i + 1).padStart(2, '0')}]</span>
                <span className="proj-filename">{proj.filename}</span>
                <span className="proj-date">
                  {proj.date}
                  {proj.distinction && <span className="proj-distinction">· Distinction</span>}
                </span>
              </div>
              <h3 className="proj-title-full">{proj.title}</h3>
              <p className="proj-desc">{proj.description}</p>
              <div className="proj-footer">
                <div className="proj-tags-row">
                  {proj.tags.map((t) => (
                    <span key={t} className="proj-tag-sm">{t}</span>
                  ))}
                </div>
                {proj.link && (
                  <a
                    href={proj.link}
                    target={proj.link.startsWith('mailto') ? undefined : '_blank'}
                    rel="noreferrer"
                    className="proj-link-sm"
                  >
                    {proj.linkLabel}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Projects() {
  return (
    <div className="institutions-wrapper">
      <div className="projects-page-header">
        <span className="projects-comment">{"// what i've done"}</span>
        <h2 className="code-header">
          <span className="keyword">print</span>
          <span className="punctuation">(</span>
          <span className="string">"projects"</span>
          <span className="punctuation">)</span>
        </h2>
      </div>

      {institutions.map((inst, idx) => (
        <InstSection
          key={inst.path}
          inst={inst}
          defaultOpen={idx === 0}
          delay={`${idx * 0.1}s`}
        />
      ))}
    </div>
  );
}
